import type { RecordLocationRef, RecordTableRow } from './RecordsList';

/**
 * Which of the two weights `Records/slaughters` treats as "the" weight for a row - the farm's own
 * default measurement basis, sent back per row rather than once per directory because a row can
 * predate a farm switching basis. Read by `useSlaughtersColumnVisibility` to decide which of the
 * live/slaughter weight column pairs defaults on.
 */
export enum SlaughterComputationBasis {
    SlaughterWeight = 0,
    LivingWeight = 1,
}

/**
 * One slaughter registration as served by `Records/slaughters`. Snake_case because it mirrors the
 * wire verbatim.
 */
export type SlaughterApiRow = {
    record_id: number;
    /** Farm-local date of the registration. `yyyy-MM-dd`. */
    date: string;
    /**
     * Farm-local Monday of the week this registration bins into, `yyyy-MM-dd`. Server-computed, so
     * the page needs no week arithmetic of its own.
     */
    week_start: string;

    farm: RecordLocationRef;
    barn: RecordLocationRef;
    /** Null when the registration was made at or above Barn level. */
    section: RecordLocationRef | null;

    number_of_pigs: number;

    /** Null when no live weight was registered for this row. */
    total_live_weight: number | null;
    avg_live_weight: number | null;
    /** Null when no slaughter weight was registered for this row. */
    total_slaughter_weight: number | null;
    avg_slaughter_weight: number | null;

    /** Null when the slaughter has not been settled yet - not the same as a settled zero. */
    discarded_pigs: number | null;
    /** Null whenever `discarded_pigs` is null or zero: there is nothing to average. */
    avg_discarded_weight: number | null;

    /** Total payment weight, as settled. Only present when a Generic settlement supplied it - null
     * for every other integrator, and for a slaughter not yet settled. */
    payment_weight: number | null;

    /** Customer name. Null when the registration carries none - never gated by directory type. */
    customer: string | null;
    /** Invoice number. Null when the registration carries none - never gated by directory type. */
    invoice_no: string | null;
    /** Classification name. Null when the slaughterhouse reported none. */
    classification: string | null;

    /** Settlement number. Null when the slaughter has not been settled yet. */
    settlement_no: string | null;
    /** Supplier number. Null when the registration carries none. */
    supplier_no: string | null;
    /** Average meat percentage, e.g. `61.1`. Null when the slaughter has not been settled yet. */
    avg_meat_percent: number | null;

    computation_basis: SlaughterComputationBasis;
    /** Full name of the user who created the registration. Null when the creator is missing, deleted/anonymized, or has no name. */
    created_by: string | null;
};

/**
 * `SlaughterApiRow`, reshaped to the field names the shared records kit and `RecordsTable` read.
 *
 * `farm`/`barn` pass through unchanged so the page renders its Location column and facet the same
 * way Sold does - through `RecordsTable`'s shared `barn` cell and `locationLabel`, faceted on
 * `barn.id` rather than a name, which can repeat across farms. `invoice_no` becomes `invoice` to
 * match the field name `RecordsTable`'s shared cell rendering already knows how to show (see
 * `SoldRow`). `avg_weight`/`total_weight` - the two fields `RecordRow`'s aggregation (the weekly
 * chart's pig count, the shared footer totals) actually reads - are left null on every row: a
 * slaughter carries two distinct weights, never one, and nothing here is positioned to guess which
 * of them a mixed-basis selection should total. The weekly chart Slaughters draws is its own fork
 * (`useSlaughtersChartOptions`) precisely because it needs a second measure (`discarded_pigs`) this
 * shared shape has no room for.
 */
export type SlaughterRow = RecordTableRow & {
    farm: RecordLocationRef;
    barn: RecordLocationRef;
    section: RecordLocationRef | null;
    total_live_weight: number | null;
    avg_live_weight: number | null;
    total_slaughter_weight: number | null;
    avg_slaughter_weight: number | null;
    discarded_pigs: number | null;
    avg_discarded_weight: number | null;
    payment_weight: number | null;
    customer: string | null;
    invoice: string | null;
    classification: string | null;
    settlement_no: string | null;
    supplier_no: string | null;
    avg_meat_percent: number | null;
    computation_basis: SlaughterComputationBasis;
    /** Full name of the user who created the registration. Null when the creator is missing, deleted/anonymized, or has no name. */
    created_by: string | null;
};

/** `SlaughterApiRow` as the shared records kit and `RecordsTable` read it. */
export function mapSlaughterRow(raw: SlaughterApiRow): SlaughterRow {
    return {
        record_id: raw.record_id,
        date: raw.date,
        week_start: raw.week_start,
        number: raw.number_of_pigs,
        avg_weight: null,
        total_weight: null,
        farm: raw.farm,
        barn: raw.barn,
        section: raw.section,
        total_live_weight: raw.total_live_weight,
        avg_live_weight: raw.avg_live_weight,
        total_slaughter_weight: raw.total_slaughter_weight,
        avg_slaughter_weight: raw.avg_slaughter_weight,
        discarded_pigs: raw.discarded_pigs,
        avg_discarded_weight: raw.avg_discarded_weight,
        payment_weight: raw.payment_weight,
        customer: raw.customer,
        invoice: raw.invoice_no,
        classification: raw.classification,
        settlement_no: raw.settlement_no,
        supplier_no: raw.supplier_no,
        avg_meat_percent: raw.avg_meat_percent,
        computation_basis: raw.computation_basis,
        created_by: raw.created_by,
    };
}
